import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Handshake, Calendar, DollarSign, FileText, Plus, Edit, Trash2, CheckCircle, XCircle } from"lucide-react";
import { toast } from"sonner";

const SubcontractorsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("all");
 const [subcontractors, setSubcontractors] = useState([]);
 const [partners, setPartners] = useState([]);
 const [loading, setLoading] = useState(false);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [editMode, setEditMode] = useState(false);
 const [editingSubId, setEditingSubId] = useState(null);
 const [formData, setFormData] = useState({
 partner_id:"",
 project_id:"",
 service_type:"",
 contract_value: 0,
 currency:"EUR",
 contract_start_date:"",
 contract_end_date:"",
 job_deadline:"",
 contract_reference:"",
 contract_document_url:"",
 payment_terms:"",
 notes:""
 });
 const [uploadingFile, setUploadingFile] = useState(false);
 const [subTasks, setSubTasks] = useState([]);

 const serviceTypes = [
"Concrete","Electrical","Plumbing","HVAC","Roofing",
"Carpentry","Painting","Landscaping","Tile Installation",
"Masonry","Steel Work","General Construction","Other"
 ];

 useEffect(() => {
 loadProjects();
 loadPartners();
 }, []);

 useEffect(() => {
 if (selectedProject && projects.length > 0) {
 loadSubcontractors();
 }
 }, [selectedProject, projects]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadPartners = async () => {
 try {
 const response = await axios.get("/partners");
 setPartners(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadSubcontractors = async () => {
 setLoading(true);
 try {
 if (selectedProject ==="all") {
 const allSubs = await Promise.all(
 projects.map(project => 
 axios.get(`/subcontractors/${project.id}`)
 .then(res => res.data.map(sub => ({ ...sub, project_name: project.name })))
 .catch(() => [])
 )
 );
 setSubcontractors(allSubs.flat());
 } else {
 const response = await axios.get(`/subcontractors/${selectedProject}`);
 const project = projects.find(p => p.id === selectedProject);
 setSubcontractors(response.data.map(sub => ({ ...sub, project_name: project?.name })));
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const openAddDialog = () => {
 if (selectedProject ==="all") {
 toast.info("Please select a specific project to add a subcontractor");
 return;
 }
 
 setEditMode(false);
 setEditingSubId(null);
 setFormData({
 partner_id:"",
 project_id: selectedProject,
 service_type:"",
 contract_value: 0,
 currency:"EUR",
 contract_start_date: new Date().toISOString().split('T')[0],
 contract_end_date:"",
 job_deadline:"",
 contract_reference:"",
 contract_document_url:"",
 payment_terms:"",
 notes:""
 });
 setDialogOpen(true);
 };

 const openEditDialog = async (sub) => {
 setEditMode(true);
 setEditingSubId(sub.id);
 setFormData({
 partner_id: sub.partner_id,
 project_id: sub.project_id,
 service_type: sub.service_type,
 contract_value: sub.contract_value,
 currency: sub.currency,
 contract_start_date: sub.contract_start_date?.split('T')[0] ||"",
 contract_end_date: sub.contract_end_date?.split('T')[0] ||"",
 job_deadline: sub.job_deadline?.split('T')[0] ||"",
 contract_reference: sub.contract_reference,
 contract_document_url: sub.contract_document_url ||"",
 payment_terms: sub.payment_terms ||"",
 notes: sub.notes ||""
 });
 
 // Load tasks for this subcontractor
 try {
 const tasksRes = await axios.get(`/subcontractors/${sub.id}/tasks`);
 setSubTasks(tasksRes.data.tasks || []);
 } catch (error) {
 console.error("Failed to load tasks", error);
 setSubTasks([]);
 }
 
 setDialogOpen(true);
 };

 const handleFileUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
 if (!allowedTypes.includes(file.type)) {
 toast.error(t("validation.required"));
 return;
 }

 if (file.size > 5 * 1024 * 1024) {
 toast.error(t("validation.required"));
 return;
 }

 const formDataUpload = new FormData();
 formDataUpload.append('file', file);

 try {
 setUploadingFile(true);
 toast.info("Uploading file...");
 const response = await axios.post('/upload', formDataUpload, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 setFormData(prev => ({ ...prev, contract_document_url: response.data.url }));
 toast.success(t("messages.saveSuccess"));
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setUploadingFile(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 const payload = {
 ...formData,
 contract_start_date: new Date(formData.contract_start_date).toISOString(),
 contract_end_date: formData.contract_end_date ? new Date(formData.contract_end_date).toISOString() : null,
 job_deadline: formData.job_deadline ? new Date(formData.job_deadline).toISOString() : null
 };

 if (editMode) {
 await axios.patch(`/subcontractors/${editingSubId}`, payload);
 toast.success(t("messages.saveSuccess"));
 } else {
 await axios.post("/subcontractors", payload);
 toast.success(t("messages.saveSuccess"));
 }
 
 setDialogOpen(false);
 setSubTasks([]);
 loadSubcontractors();
 } catch (error) {
 toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'add'} subcontractor`);
 }
 };

 const updateStatus = async (subId, status) => {
 try {
 await axios.patch(`/subcontractors/${subId}`, { status });
 toast.success(`Status updated to ${status}`);
 loadSubcontractors();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const deleteSubcontractor = async (subId) => {
 if (!window.confirm("Are you sure you want to remove this subcontractor from the project?")) return;
 
 try {
 await axios.delete(`/subcontractors/${subId}`);
 toast.success(t("messages.deleteSuccess"));
 loadSubcontractors();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat("en-US", {
 style:"currency",
 currency: currency,
 minimumFractionDigits: 0,
 maximumFractionDigits: 0,
 }).format(amount);
 };

 const formatDate = (dateString) => {
 if (!dateString) return"N/A";
 return new Date(dateString).toLocaleDateString("en-US", {
 year:"numeric",
 month:"short",
 day:"numeric",
 });
 };

 const getStatusColor = (status) => {
 const colors = {
 active:"bg-emerald-500/10 text-emerald-700",
 completed:"bg-blue-500/10 text-blue-700",
 terminated:"bg-rose-500/10 text-rose-700",
 };
 return colors[status] ||"bg-gray-500/10 text-gray-700";
 };

 const isDeadlineApproaching = (deadline) => {
 if (!deadline) return false;
 const daysUntil = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
 return daysUntil <= 7 && daysUntil >= 0;
 };

 const isDeadlineOverdue = (deadline) => {
 if (!deadline) return false;
 return new Date(deadline) < new Date();
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {t('subcontractors.title')}
 </h1>
 <p className="text-slate-600 mt-1">{t('subcontractors.subtitle')}</p>
 </div>
 <div className="flex gap-3 items-center">
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue placeholder={t('common.selectProject')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">{t('common.allProjects')}</SelectItem>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 {["owner","management"].includes(user.role) && (
 <Button onClick={openAddDialog}>
 <Plus className="w-4 h-4 mr-2"/>
 {t('subcontractors.assignSubcontractor')}
 </Button>
 )}
 </div>
 </div>

 {/* Subcontractors List */}
 {loading ? (
 <div className="text-center py-12 text-sm text-slate-500">{t('common.loading')}</div>
 ) : subcontractors.length === 0 ? (
 <Card>
 <CardContent className="text-center py-12 text-slate-600">
 <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p>{t('subcontractors.noSubcontractors')}</p>
 <p className="text-sm text-slate-400 mt-1">{t('subcontractors.assignHint')}</p>
 </CardContent>
 </Card>
 ) : (
 <div className="grid gap-4">
 {subcontractors.map((sub) => {
 const partner = sub.partner_details || {};
 
 return (
 <Card key={sub.id} className="hover:shadow-md transition-shadow">
 <CardHeader>
 <div className="flex items-start justify-between">
 <div className="flex items-center gap-3">
 <Handshake className="w-6 h-6 text-blue-600"/>
 <div>
 <CardTitle className="text-xl">{partner.company_name || t('common.unknown')}</CardTitle>
 <p className="text-sm text-slate-600 mt-1">
 {sub.project_name || t('common.unknown')} • {sub.service_type}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <Badge className={getStatusColor(sub.status)}>
 {t(`subcontractors.${sub.status}`)}
 </Badge>
 {sub.status ==="active"&& (
 <div className="flex gap-1">
 <Button
 size="sm"
 variant="outline"
 onClick={() => updateStatus(sub.id,"completed")}
 className="h-7 text-xs"
 >
 <CheckCircle className="w-3 h-3 mr-1"/>
 {t('subcontractors.markCompleted')}
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => updateStatus(sub.id,"terminated")}
 className="h-7 text-xs text-red-600"
 >
 <XCircle className="w-3 h-3 mr-1"/>
 {t('subcontractors.markCancelled')}
 </Button>
 </div>
 )}
 </div>
 </div>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {/* Contract Value */}
 <div>
 <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
 <DollarSign className="w-4 h-4"/>
 <span>{t('subcontractors.contractValue')}</span>
 </div>
 <div className="font-semibold text-slate-900">
 {formatCurrency(sub.contract_value, sub.currency)}
 </div>
 </div>

 {/* Start Date */}
 <div>
 <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
 <Calendar className="w-4 h-4"/>
 <span>{t('projects.startDate')}</span>
 </div>
 <div className="font-semibold text-slate-900">
 {formatDate(sub.contract_start_date)}
 </div>
 </div>

 {/* Job Deadline */}
 <div>
 <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
 <Calendar className="w-4 h-4"/>
 <span>{t('subcontractors.deadline')}</span>
 </div>
 <div className="font-semibold text-slate-900">
 {formatDate(sub.job_deadline)}
 {isDeadlineOverdue(sub.job_deadline) && (
 <Badge className="ml-2 bg-rose-500/10 text-rose-700 text-xs">{t('common.overdue')}</Badge>
 )}
 {isDeadlineApproaching(sub.job_deadline) && !isDeadlineOverdue(sub.job_deadline) && (
 <Badge className="ml-2 bg-amber-500/10 text-amber-700 text-xs">{t('common.dueSoon')}</Badge>
 )}
 </div>
 </div>

 {/* Contract Reference */}
 <div>
 <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
 <FileText className="w-4 h-4"/>
 <span>{t('subcontractors.contractReference')}</span>
 </div>
 <div className="font-semibold text-slate-900">
 {sub.contract_reference}
 </div>
 </div>
 </div>

 {/* Contact & Contract Document */}
 <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
 <div>
 <div className="text-xs text-sm text-slate-500">{t('partners.contactPerson')}</div>
 <div className="text-sm font-medium text-slate-900">{partner.contact_person}</div>
 <div className="text-xs text-sm text-slate-500">{partner.email} • {partner.phone}</div>
 </div>
 {sub.contract_document_url && (
 <div>
 <div className="text-xs text-sm text-slate-500">{t('documents.title')}</div>
 <a 
 href={sub.contract_document_url} 
 target="_blank"
 rel="noopener noreferrer"
 className="text-sm text-blue-600 hover:text-blue-800 underline"
 >
 {t('documents.viewDocument')}
 </a>
 </div>
 )}
 </div>

 {sub.payment_terms && (
 <div className="mt-3 text-sm bg-white/50 border border-slate-200 p-2 rounded">
 <span className="font-medium text-slate-900">{t('subcontractors.paymentTerms')}:</span> 
 <span className="text-[#334155]"> {sub.payment_terms}</span>
 </div>
 )}

 {sub.notes && (
 <div className="mt-2 text-sm text-slate-600 bg-white/50 p-2 rounded">
 <span className="font-medium">{t('common.notes')}:</span> {sub.notes}
 </div>
 )}

 {/* Actions - Only for owner/management */}
 {["owner","management"].includes(user.role) && (
 <div className="flex gap-2 mt-4 pt-3 border-t">
 <Button
 size="sm"
 variant="outline"
 onClick={() => openEditDialog(sub)}
 >
 <Edit className="w-3 h-3 mr-1"/>
 {t('common.edit')}
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => deleteSubcontractor(sub.id)}
 className="hover:bg-rose-50/80 hover:text-rose-700 hover:border-rose-300"
 >
 <Trash2 className="w-3 h-3 mr-1"/>
 {t('common.remove')}
 </Button>
 </div>
 )}
 </CardContent>
 </Card>
 );
 })}
 </div>
 )}

 {/* Add/Edit Subcontractor Dialog */}
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editMode ? t('subcontractors.editSubcontractor') : t('subcontractors.assignSubcontractor')}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="partner_id">{t('subcontractors.selectPartner')} *</Label>
 <Select
 value={formData.partner_id}
 onValueChange={(value) => setFormData({ ...formData, partner_id: value })}
 disabled={editMode}
 >
 <SelectTrigger>
 <SelectValue placeholder="Choose a partner from your directory"/>
 </SelectTrigger>
 <SelectContent>
 {partners.map((partner) => (
 <SelectItem key={partner.id} value={partner.id}>
 {partner.company_name} - {partner.specialization}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 {!editMode && partners.length === 0 && (
 <p className="text-xs text-red-600 mt-1">
 No partners available. Please add partners in the Partners page first.
 </p>
 )}
 </div>
 
 {/* Show tasks in edit mode, service type in create mode */}
 {editMode && subTasks.length > 0 ? (
 <div className="bg-blue-50/80 border border-blue-200/60 p-3 rounded-sm">
 <Label className="text-sm font-semibold text-blue-900">Responsible for Tasks:</Label>
 <div className="mt-2 space-y-1">
 {subTasks.map((task) => (
 <div key={task.id} className="text-sm text-blue-800 flex items-center gap-2">
 <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
 {task.job_name} {task.area_m2 ? `(${task.area_m2} m²)` : ''}
 </div>
 ))}
 </div>
 </div>
 ) : (
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="service_type">Service Type *</Label>
 <Select
 value={formData.service_type}
 onValueChange={(value) => setFormData({ ...formData, service_type: value })}
 disabled={editMode}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select service"/>
 </SelectTrigger>
 <SelectContent>
 {serviceTypes.map((type) => (
 <SelectItem key={type} value={type}>{type}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="contract_reference">Contract Reference</Label>
 <Input
 id="contract_reference"
 value={formData.contract_reference}
 onChange={(e) => setFormData({ ...formData, contract_reference: e.target.value })}
 placeholder="Auto-generated if left empty"
 />
 <p className="text-[11px] text-slate-500 mt-1">
 Format: BuildingCode-YearMonth-Sequence (e.g., KV4-202512-1)
 </p>
 </div>
 </div>
 )}
 
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="contract_value">Contract Value *</Label>
 <Input
 id="contract_value"
 type="number"
 step="0.01"
 value={formData.contract_value}
 onChange={(e) => setFormData({ ...formData, contract_value: parseFloat(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="currency">{t("projectProgress.currency")}</Label>
 <Select
 value={formData.currency}
 onValueChange={(value) => setFormData({ ...formData, currency: value })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="EUR">EUR</SelectItem>
 <SelectItem value="USD">USD</SelectItem>
 <SelectItem value="MKD">MKD</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 
 <div className="grid grid-cols-3 gap-4">
 <div>
 <Label htmlFor="contract_start_date">Start Date *</Label>
 <Input
 id="contract_start_date"
 type="date"
 value={formData.contract_start_date}
 onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="contract_end_date">End Date</Label>
 <Input
 id="contract_end_date"
 type="date"
 value={formData.contract_end_date}
 onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
 />
 </div>
 <div>
 <Label htmlFor="job_deadline">Job Deadline</Label>
 <Input
 id="job_deadline"
 type="date"
 value={formData.job_deadline}
 onChange={(e) => setFormData({ ...formData, job_deadline: e.target.value })}
 />
 </div>
 </div>
 
 <div>
 <Label htmlFor="contract_document">Contract Document</Label>
 <Input
 id="contract_document"
 type="file"
 accept=".pdf,.jpg,.jpeg,.png"
 onChange={handleFileUpload}
 disabled={uploadingFile}
 />
 {uploadingFile && (
 <p className="text-sm text-blue-600 mt-1">⏳ Uploading...</p>
 )}
 {formData.contract_document_url && !uploadingFile && (
 <p className="text-sm text-green-600 mt-1">
 ✓ File uploaded: <a href={formData.contract_document_url} target="_blank"rel="noopener noreferrer"className="underline">View</a>
 </p>
 )}
 <p className="text-[11px] text-slate-500 mt-1">
 Upload contract document (PDF, JPG, PNG - Max 5MB)
 </p>
 </div>
 
 <div>
 <Label htmlFor="payment_terms">Payment Terms</Label>
 <Textarea
 id="payment_terms"
 value={formData.payment_terms}
 onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
 placeholder="e.g., 50% upfront, 50% on completion"
 rows={2}
 />
 </div>
 
 <div>
 <Label htmlFor="notes">{t("common.notes")}</Label>
 <Textarea
 id="notes"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 rows={3}
 />
 </div>
 
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1"disabled={!editMode && partners.length === 0}>
 {editMode ?"Update Subcontractor":"Assign to Project"}
 </Button>
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default SubcontractorsPage;
